import { createApi, fetchBaseQuery, BaseQueryFn } from "@reduxjs/toolkit/query/react";
import { getApiConfig } from "@/config/api";
import { getRequestLanguage } from "@/utils/requestLanguage";
import type { Unit, ApiResponse, CreateUnitData, UpdateUnitData } from "@/types";

// Create a dynamic base query for unit API
const dynamicUnitBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const config = getApiConfig();

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (headers, { getState }) => {
      let token = (getState() as any).auth.token;
      if (!token) {
        token = localStorage.getItem("token");
      }
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      headers.set("accept-language", getRequestLanguage());
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

export const unitApi = createApi({
  reducerPath: "unitApi",
  baseQuery: dynamicUnitBaseQuery,
  tagTypes: ["Unit"],
  endpoints: (builder) => ({
    // Get all units for a store
    getUnits: builder.query<
      ApiResponse<Unit[]>,
      { storeId: string; includeInactive?: boolean; includeDeleted?: boolean }
    >({
      query: (params) => ({
        url: "/units",
        params,
      }),
      providesTags: ["Unit"],
    }),

    // Get single unit
    getUnit: builder.query<ApiResponse<Unit>, string>({
      query: (id) => `/units/${id}`,
      providesTags: (_, __, id) => [{ type: "Unit", id }],
    }),

    // Create unit
    createUnit: builder.mutation<ApiResponse<Unit>, CreateUnitData>({
      query: (data) => ({
        url: "/units",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Unit"],
    }),

    // Update unit
    updateUnit: builder.mutation<ApiResponse<Unit>, { id: string; data: UpdateUnitData }>({
      query: ({ id, data }) => ({
        url: `/units/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: "Unit", id }, "Unit"],
    }),

    // Delete unit
    deleteUnit: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `/units/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Unit"],
    }),
  }),
});

export const {
  useGetUnitsQuery,
  useGetUnitQuery,
  useCreateUnitMutation,
  useUpdateUnitMutation,
  useDeleteUnitMutation,
} = unitApi;
